import React, { useState, useContext, useEffect, useCallback } from 'react';
import yaml from 'js-yaml';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import {
  Box, Grid, GridItem, Button, Table, Thead, Tbody, Tr, Th, Td, Heading, Text,
  useToast, Flex, Spinner, Badge, IconButton, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Textarea,
  Tabs, TabList, Tab, TabPanels, TabPanel, FormControl, FormLabel, Select, Switch,
  VStack, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Input
} from "@chakra-ui/react";
import { RepeatIcon, EditIcon, ViewIcon, Search2Icon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import ClientConfigs from './ClientConfigs';

function ConfigAdmin() {
  const [orgConfigs, setOrgConfigs] = useState([]);
  const [menuConfigs, setMenuConfigs] = useState([]);

  const [isLoadingOrgConfigs, setIsLoadingOrgConfigs] = useState(false);
  const [isLoadingMenuConfigs, setIsLoadingMenuConfigs] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [editingConfig, setEditingConfig] = useState(null);
  const [configJson, setConfigJson] = useState('');
  const [editMode, setEditMode] = useState(''); // 'org' or 'client'
  const [showInactive, setShowInactive] = useState(false); // Global toggle for showing inactive configurations
  const [isViewMode, setIsViewMode] = useState(false);
  const [comparisonData, setComparisonData] = useState({ active: null, historical: null });
  const [refreshClientConfigsKey, setRefreshClientConfigsKey] = useState(0);

  // Snapshot states
  const [snapshotOrgs, setSnapshotOrgs] = useState([]);
  const [selectedSnapshotOrgId, setSelectedSnapshotOrgId] = useState('');
  const [snapshotOrgConfig, setSnapshotOrgConfig] = useState(null);
  const [snapshotClientConfigs, setSnapshotClientConfigs] = useState({});
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);

  // Create modal states
  const [orgs, setOrgs] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedOrgForCreate, setSelectedOrgForCreate] = useState('');
  const [selectedClientForCreate, setSelectedClientForCreate] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isCompareOpen, onOpen: onCompareOpen, onClose: onCompareClose } = useDisclosure();
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const loadMenuConfigs = useCallback(async () => {
    setIsLoadingMenuConfigs(true);
    try {
      const response = await callApi('/admin/menu/org-menus', accessToken, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        setMenuConfigs(data);
      } else {
        throw new Error(data.error || 'Failed to fetch menu configurations');
      }
    } catch (err) {
      toast({ title: 'Error loading menu configurations', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsLoadingMenuConfigs(false);
    }
  }, [accessToken, toast]);

  const loadOrgConfigs = useCallback(async () => {
    setIsLoadingOrgConfigs(true);
    try {
      const response = await callApi('/admin/config/org-configurations', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (response.ok) {
        setOrgConfigs(data);
      } else {
        throw new Error(data.error || 'Failed to fetch org configurations');
      }
    } catch (err) {
      toast({ title: 'Error loading org configurations', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsLoadingOrgConfigs(false);
    }
  }, [accessToken, toast]);

  const loadOrgsForCreate = useCallback(async () => {
    setIsLoadingOrgs(true);
    try {
      const response = await callApi('/admin/config/orgs', accessToken, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        setOrgs(data);
      } else {
        throw new Error(data.error || 'Failed to fetch orgs');
      }
    } catch (err) {
      toast({ title: 'Error loading orgs', description: err.message, status: 'error' });
    } finally {
      setIsLoadingOrgs(false);
    }
  }, [accessToken, toast]);

  const loadClientsForCreate = useCallback(async (orgId) => {
    if (!orgId) return;
    setIsLoadingClients(true);
    try {
      const response = await callApi(`/admin/config/clients/${orgId}`, accessToken, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        setClients(data);
      } else {
        throw new Error(data.error || 'Failed to fetch clients');
      }
    } catch (err) {
      toast({ title: 'Error loading clients', description: err.message, status: 'error' });
    } finally {
      setIsLoadingClients(false);
    }
  }, [accessToken, toast]);

  useEffect(() => {
    loadOrgConfigs();
    loadMenuConfigs();
  }, [loadOrgConfigs, loadMenuConfigs]);

  // Snapshot functions
  const loadSnapshotOrgs = useCallback(async () => {
    try {
      const response = await callApi('/admin/config/orgs-with-client-config-counts', accessToken);
      const data = await response.json();
      if (response.ok) {
        setSnapshotOrgs(data);
      } else {
        throw new Error(data.error || 'Failed to fetch orgs');
      }
    } catch (err) {
      toast({ title: 'Error loading snapshot orgs', description: err.message, status: 'error', duration: 5000, isClosable: true });
    }
  }, [accessToken, toast]);

  const loadSnapshotData = useCallback(async (orgId) => {
    if (!orgId) {
      setSnapshotOrgConfig(null);
      setSnapshotClientConfigs({});
      return;
    }
    setIsLoadingSnapshot(true);
    try {
      // Fetch all org configs and filter active for this org
      const orgResp = await callApi('/admin/config/org-configurations', accessToken);
      const allOrgConfigs = await orgResp.json();
      if (orgResp.ok) {
        const activeOrgConfig = allOrgConfigs.filter(c => parseInt(c.org_id) === parseInt(orgId) && c.active)[0] || null;
        setSnapshotOrgConfig(activeOrgConfig);
      } else {
        throw new Error('Failed to fetch org configs');
      }

      // Fetch client configs for org, group active by client_id
      const clientResp = await callApi(`/admin/config/client-configurations/${orgId}`, accessToken);
      const allClientConfigs = await clientResp.json();
      if (clientResp.ok) {
        const grouped = {};
        allClientConfigs.forEach(c => {
          if (c.active && !grouped[c.client_id]) {
            grouped[c.client_id] = c;
          }
        });
        setSnapshotClientConfigs(grouped);
      } else {
        throw new Error('Failed to fetch client configs');
      }
    } catch (err) {
      toast({ title: 'Error loading snapshot', description: err.message, status: 'error', duration: 5000, isClosable: true });
      setSnapshotOrgConfig(null);
      setSnapshotClientConfigs({});
    } finally {
      setIsLoadingSnapshot(false);
    }
  }, [accessToken, toast]);

  useEffect(() => {
    loadSnapshotOrgs();
  }, [loadSnapshotOrgs]);

  const handleEdit = (config, mode, viewOnly = false) => {
    setEditingConfig(config);
    setEditMode(mode);
    setIsViewMode(viewOnly);
    try {
      const configData = mode === 'menu' ? config.menu_structure : config.configuration;
      setConfigJson(yaml.dump(configData || {}, {
        lineWidth: -1,  // Disable line wrapping
        noRefs: true,   // Prevent reference anchors
        skipInvalid: true
      }));
    } catch (e) {
      console.error("Error converting JSON to YAML", e);
      setConfigJson("# Invalid configuration data");
    }
    onOpen();
  };

  const handleCompareToActive = (activeConfig, historicalConfig) => {
    setComparisonData({ active: activeConfig, historical: historicalConfig });
    onCompareOpen();
  };

  const getConfigTemplate = () => {
    const template = {
      coding_configurations: {
        IONM: {
          diagnosis_analysis_prompt_modifier: {
            type: "add",
            value: "For IONM cases, pay extremely close attention to any mention of neurological deficits, radiculopathy, or myelopathy as these are critical for justifying nerve monitoring."
          },
          optimal_coding_selection_criteria: {
            type: "add",
            value: "Additionally, for IONM, prioritize ICD-10 codes that have a direct anatomical link to the surgical site mentioned in the operative report."
          }
        },
        Gastro: {
          diagnosis_analysis_prompt_modifier: {
            type: "add",
            value: "For Gastroenterology cases, findings from pathology reports are considered definitive evidence. Give these findings the highest weight."
          },
          optimal_coding_selection_criteria: {
            type: "add",
            value: "For Gastro cases, prioritize codes that reflect the most specific pathological findings documented."
          }
        },
        Clinical: {
          diagnosis_analysis_prompt_modifier: {
            type: "add",
            value: "For Clinical cases, focus on comprehensive assessment of all documented symptoms and examination findings."
          }
        },
        general_hint: {
          type: "add",
          value: "General guidance that applies to all case types when more specific hints are not available."
        }
      },
      case_type_hint: {
        type: "add",
        value: "Important: if the case mentions Emergency Department, select the Clinical case type. Consider procedure complexity and documentation patterns."
      },
      processor_overrides: {
        "IONM Modern": "IONM Modern No NCV No BAEP",
        "Custom Processor Name": "Mapped Processor Name"
      }
    };
    return yaml.dump(template, {
      lineWidth: -1,  // Disable line wrapping
      noRefs: true,   // Prevent reference anchors
      skipInvalid: true
    });
  };

  const handleCreateNew = (mode) => {
    setEditMode(mode);
    setEditingConfig(null);
    setConfigJson(getConfigTemplate());
    setSelectedOrgForCreate('');
    setSelectedClientForCreate('');
    setNewClientName('');
    setClients([]);
    loadOrgsForCreate();
    onCreateOpen();
  };

  const handleOrgChangeForCreate = (e) => {
    const orgId = e.target.value;
    setSelectedOrgForCreate(orgId);
    setSelectedClientForCreate('');
    setClients([]);
    if (orgId) {
      loadClientsForCreate(orgId);
    }
  };

  const handleSave = async () => {
    try {
      yaml.load(configJson);
    } catch (error) {
      toast({ title: 'Invalid YAML/JSON', description: 'Th configuration is not valid YAML or JSON.', status: 'error', duration: 5000, isClosable: true });
      return;
    }

    setIsSaving(true);
    let url, body;
    if (editMode === 'org') {
      url = '/admin/config/org-configurations';
      body = {
        org_id: editingConfig ? editingConfig.org_id : selectedOrgForCreate,
        configuration: configJson,
        previous_config_id: editingConfig ? editingConfig.id : null,
      };
    } else if (editMode === 'client') {
      url = '/admin/config/client-configurations';
      body = {
        client_id: editingConfig ? editingConfig.client_id : selectedClientForCreate,
        configuration: configJson,
        previous_config_id: editingConfig ? editingConfig.id : null,
      };
    } else if (editMode === 'menu') {
        url = '/admin/menu/org-menus';
        body = {
            org_id: editingConfig ? editingConfig.org_id : selectedOrgForCreate,
            menu_structure: configJson,
        };
    }

    try {
      const response = await callApi(url, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok) {
        toast({ title: 'Success', description: editingConfig ? 'Configuration updated successfully.' : 'Configuration created successfully.', status: 'success' });
        onClose();
        onCreateClose();
        if (editMode === 'org') {
          loadOrgConfigs();
        } else if (editMode === 'client') {
          setRefreshClientConfigsKey(k => k + 1);
        } else if (editMode === 'menu') {
          loadMenuConfigs();
        }
      } else {
        throw new Error(data.error || 'Failed to save configuration');
      }
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateSave = async () => {
    // Validation for create mode
    if (editMode === 'org' && !selectedOrgForCreate) {
      toast({ title: 'Missing selection', description: 'Please select an organization.', status: 'error' });
      return;
    }
    if (editMode === 'client' && (!selectedOrgForCreate || !newClientName.trim())) {
      toast({ title: 'Missing selection', description: 'Please select an organization and provide a new client name.', status: 'error' });
      return;
    }

    try {
      yaml.load(configJson);
    } catch (error) {
      toast({ title: 'Invalid YAML/JSON', description: 'The configuration is not valid YAML or JSON.', status: 'error' });
      return;
    }

    setIsSaving(true);
    let url, body;
    if (editMode === 'org') {
      url = '/admin/config/org-configurations';
      body = {
        org_id: selectedOrgForCreate,
        configuration: configJson,
        previous_config_id: null,
      };
    } else if (editMode === 'client') {
      url = '/admin/config/client-with-configuration';
      body = {
        org_id: selectedOrgForCreate,
        new_client_name: newClientName.trim(),
        configuration: configJson,
      };
    } else if (editMode === 'menu') {
        url = '/admin/menu/org-menus';
        body = {
            org_id: selectedOrgForCreate,
            menu_structure: configJson,
        };
    }

    try {
      const response = await callApi(url, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok) {
        toast({ title: 'Success', description: 'Configuration created successfully.', status: 'success' });
        onCreateClose();
        if (editMode === 'org') {
          loadOrgConfigs();
        } else if (editMode === 'client') {
          setRefreshClientConfigsKey(k => k + 1);
        } else if (editMode === 'menu') {
          loadMenuConfigs();
        }
      } else {
        throw new Error(data.error || 'Failed to create configuration');
      }
    } catch (err) {
      toast({ title: 'Creation failed', description: err.message, status: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const renderOrgConfigsTable = () => {
    // Filter configs based on showInactive toggle
    const filteredConfigs = showInactive ? orgConfigs : orgConfigs.filter(config => config.active);
    
    // Group configs by org and find the most recent for each (from all configs, not just filtered)
    const orgGroups = orgConfigs.reduce((groups, config) => {
      if (!groups[config.org_id] || new Date(config.created_at) > new Date(groups[config.org_id].created_at)) {
        groups[config.org_id] = config;
      }
      return groups;
    }, {});

    return (
      <Box overflowX="auto" bg="white" borderRadius="md" boxShadow="sm">
        <Table variant="simple">
          <Thead><Tr><Th>Organization</Th><Th>Created At</Th><Th>Status</Th><Th>Actions</Th></Tr></Thead>
          <Tbody>
            {filteredConfigs.map((config) => {
              const isMostRecent = orgGroups[config.org_id]?.id === config.id;
              return (
                <Tr key={config.id || `config-${config.org_id}`}>
                  <Td>{config.org_name}</Td>
                  <Td>{new Date(config.created_at).toLocaleString()}</Td>
                  <Td>
                    <Badge colorScheme={config.active ? 'green' : 'gray'}>
                      {config.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    {isMostRecent ? (
                      <IconButton 
                        icon={<EditIcon />} 
                        size="sm" 
                        onClick={() => handleEdit(config, 'org', false)} 
                        aria-label="Edit org config"
                        title="Edit configuration (creates new version)"
                      />
                    ) : (
                      <IconButton 
                        icon={<ViewIcon />} 
                        size="sm" 
                        onClick={() => handleEdit(config, 'org', true)} 
                        aria-label="View org config"
                        title="View configuration"
                      />
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  };

  const renderMenuConfigsTable = () => {
    // Filter configs based on showInactive toggle
    const filteredConfigs = showInactive ? menuConfigs : menuConfigs.filter(config => config.active);
    
    // Group configs by org and find the most recent for each (from all configs, not just filtered)
    const menuGroups = menuConfigs.reduce((groups, config) => {
      if (!groups[config.org_id] || new Date(config.created_at) > new Date(groups[config.org_id].created_at)) {
        groups[config.org_id] = config;
      }
      return groups;
    }, {});

    return (
      <Box overflowX="auto" bg="white" borderRadius="md" boxShadow="sm">
        <Table variant="simple">
          <Thead><Tr><Th>Organization</Th><Th>Created At</Th><Th>Status</Th><Th>Actions</Th></Tr></Thead>
          <Tbody>
            {filteredConfigs.map((config) => {
              const isMostRecent = menuGroups[config.org_id]?.id === config.id;
              return (
                <Tr key={config.id || `menu-config-${config.org_id}`}>
                  <Td>{config.org_name}</Td>
                  <Td>{new Date(config.created_at).toLocaleString()}</Td>
                  <Td>
                    <Badge colorScheme={config.active ? 'green' : 'gray'}>
                      {config.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    {isMostRecent ? (
                      <IconButton 
                        icon={<EditIcon />} 
                        size="sm" 
                        onClick={() => handleEdit(config, 'menu', false)} 
                        aria-label="Edit menu config"
                        title="Edit menu (creates new version)"
                      />
                    ) : (
                      <IconButton 
                        icon={<ViewIcon />} 
                        size="sm" 
                        onClick={() => handleEdit(config, 'menu', true)} 
                        aria-label="View menu config"
                        title="View menu"
                      />
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  };

  const diffViewerStyles = {
    content: {
      textAlign: 'left',
    },
  };
  const yamlDumpOptions = { lineWidth: -1, noRefs: true, skipInvalid: true };

  return (
    <Box textAlign="center" fontSize="xl">
      <Grid templateAreas={`"header header" "main main"`} gridTemplateRows={'60px 1fr'} h='100vh'>
        <HeaderMenus />
        <GridItem bg='gray.50' padding={6} area={'main'} overflowY="auto">
          <Box maxWidth="1200px" margin="0 auto">
            <Flex justifyContent="space-between" alignItems="center" mb={6}>
                <Heading as="h1" size="lg" textAlign="left">Configuration Management</Heading>
                <Flex alignItems="center" gap={4}>
                  <Flex alignItems="center" gap={2}>
                    <Text fontSize="sm">Show inactive:</Text>
                    <Switch 
                      isChecked={showInactive} 
                      onChange={(e) => setShowInactive(e.target.checked)}
                      colorScheme="blue"
                    />
                  </Flex>
                  <Button leftIcon={<RepeatIcon />} onClick={() => { 
                    loadOrgConfigs(); 
                    setRefreshClientConfigsKey(k => k + 1); 
                    loadMenuConfigs(); 
                    loadSnapshotOrgs(); 
                    setSelectedSnapshotOrgId(''); 
                    setSnapshotOrgConfig(null); 
                    setSnapshotClientConfigs({}); 
                  }}>Refresh All</Button>
                </Flex>
            </Flex>

            <Tabs isFitted variant="enclosed">
              <TabList mb="1em">
                <Tab>Organization Configurations</Tab>
                <Tab>Client Configurations</Tab>
                <Tab>Menu Structures</Tab>
                <Tab>Org Snapshot</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <Flex justifyContent="space-between" alignItems="center" mb={4}>
                    <Heading as="h3" size="md">Organization Configurations</Heading>
                    <Button colorScheme="blue" onClick={() => handleCreateNew('org')}>Create New Configuration</Button>
                  </Flex>
                  {isLoadingOrgConfigs ? <Spinner /> : orgConfigs.length > 0 ? renderOrgConfigsTable() : <Text>No configurations found.</Text>}
                </TabPanel>
                <TabPanel>
                  <Flex justifyContent="space-between" alignItems="center" mb={4}>
                    <Heading as="h3" size="md">Client Configurations</Heading>
                    <Button colorScheme="blue" onClick={() => handleCreateNew('client')}>Create New Configuration</Button>
                  </Flex>
                  <ClientConfigs 
                    showInactive={showInactive}
                    onEdit={handleEdit}
                    onCompare={handleCompareToActive}
                    refreshKey={refreshClientConfigsKey}
                  />
                </TabPanel>
                <TabPanel>
                  <Flex justifyContent="space-between" alignItems="center" mb={4}>
                    <Heading as="h3" size="md">Menu Structures</Heading>
                    <Button colorScheme="blue" onClick={() => handleCreateNew('menu')}>Create New Menu</Button>
                  </Flex>
                  {isLoadingMenuConfigs ? <Spinner /> : menuConfigs.length > 0 ? renderMenuConfigsTable() : <Text>No menus found.</Text>}
                </TabPanel>
                <TabPanel>
                  <Box>
                    <Heading as="h3" size="md" mb={4}>Org Snapshot</Heading>
                    
                    <FormControl mb={4}>
                      <FormLabel>Organization</FormLabel>
                      <Select
                        placeholder="Select an organization to view snapshot..."
                        value={selectedSnapshotOrgId}
                        onChange={(e) => {
                          const orgId = e.target.value;
                          setSelectedSnapshotOrgId(orgId);
                          loadSnapshotData(orgId);
                        }}
                        isDisabled={isLoadingSnapshot}
                      >
                        {snapshotOrgs.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name} ({org.client_count || 0} clients)
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedSnapshotOrgId && (
                      <>
                        {isLoadingSnapshot ? (
                          <Flex justify="center" p={8}>
                            <Spinner />
                          </Flex>
                        ) : (
                          <Box>
                            {/* Org Config */}
                            <Heading size="sm" mt={6} mb={3}>Organization Config (Active)</Heading>
                            {snapshotOrgConfig ? (
                              <Box mb={8} p={4} bg="gray.50" borderRadius="md" borderWidth={1}>
                                <Text fontSize="sm" color="gray.500" mb={3}>
                                  Updated: {new Date(snapshotOrgConfig.created_at).toLocaleString()}
                                </Text>
                                <Textarea
                                  value={yaml.dump(snapshotOrgConfig.configuration || {}, { lineWidth: -1, noRefs: true })}
                                  readOnly
                                  fontFamily="monospace"
                                  fontSize="sm"
                                  height="250px"
                                  resize="vertical"
                                />
                              </Box>
                            ) : (
                              <Text mb={8} color="gray.500">No active organization config found.</Text>
                            )}

                            {/* Client Configs */}
                            <Heading size="sm" mb={3}>Client Configs (Active)</Heading>
                            {Object.keys(snapshotClientConfigs).length > 0 ? (
                              <Accordion defaultIndex={[]} allowMultiple allowToggle>
                                {Object.values(snapshotClientConfigs).map((config) => (
                                  <AccordionItem key={config.client_id}>
                                    <h2>
                                      <AccordionButton>
                                        <Box flex="1" textAlign="left">
                                          {config.client_name || `Client ${config.client_id}`}
                                        </Box>
                                        <AccordionIcon />
                                      </AccordionButton>
                                    </h2>
                                    <AccordionPanel pb={4}>
                                      <Text fontSize="sm" color="gray.500" mb={3}>
                                        Updated: {new Date(config.created_at).toLocaleString()}
                                      </Text>
                                      <Textarea
                                        value={yaml.dump(config.configuration || {}, { lineWidth: -1, noRefs: true })}
                                        readOnly
                                        fontFamily="monospace"
                                        fontSize="sm"
                                        height="200px"
                                        resize="vertical"
                                      />
                                    </AccordionPanel>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            ) : (
                              <Text color="gray.500">No active client configs found for this org.</Text>
                            )}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </GridItem>
      </Grid>

      {/* Edit/View Configuration Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isViewMode ? 'View Configuration' : 'Edit Configuration'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!isViewMode && (
              <Text fontSize="sm" color="gray.600" mb={4}>
                {editingConfig?.active 
                  ? "Editing the active configuration. Saving will create a new version and deactivate all previous versions."
                  : "Editing an inactive configuration. Saving will create a new active version and deactivate all previous versions."
                }
              </Text>
            )}
            <Textarea 
              value={configJson} 
              onChange={(e) => setConfigJson(e.target.value)} 
              placeholder='Enter valid YAML or JSON configuration...' 
              height="400px" 
              fontFamily="monospace"
              isReadOnly={isViewMode}
            />
          </ModalBody>
          <ModalFooter>
            {isViewMode ? (
              <Button variant="ghost" onClick={onClose}>Close</Button>
            ) : (
              <>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving}>Save New Version</Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create New Configuration Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Configuration</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={4}>
              <FormControl isRequired mb={4}>
                <FormLabel>Organization</FormLabel>
                <Select 
                  placeholder="Select organization..." 
                  value={selectedOrgForCreate} 
                  onChange={handleOrgChangeForCreate}
                  isDisabled={isLoadingOrgs}
                >
                  {orgs.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </Select>
              </FormControl>

              {editMode === 'client' && (
                <FormControl isRequired mb={4}>
                  <FormLabel>New Client Name</FormLabel>
                  <Input
                    placeholder="Enter new client name..."
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    isDisabled={!selectedOrgForCreate}
                  />
                </FormControl>
              )}
            </Box>

            <FormControl>
              <FormLabel>Configuration (YAML/JSON)</FormLabel>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Template with common configuration options is pre-loaded. Modify the values as needed, or remove sections that don't apply.
              </Text>
              <Textarea 
                value={configJson} 
                onChange={(e) => setConfigJson(e.target.value)} 
                placeholder='Enter valid YAML or JSON configuration...' 
                height="300px" 
                fontFamily="monospace"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleCreateSave} isLoading={isSaving}>Create Configuration</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Compare to Active Modal */}
      <Modal isOpen={isCompareOpen} onClose={onCompareClose}>
        <ModalOverlay />
        <ModalContent maxWidth="90vw">
          <ModalHeader>Comparing Configurations</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {comparisonData.active && comparisonData.historical && (
              <>
                <Text mb={4}>
                  Showing differences between the current <strong>active</strong> version and the version from <strong>{new Date(comparisonData.historical.created_at).toLocaleString()}</strong>.
                </Text>
                <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                  <ReactDiffViewer
                    oldValue={yaml.dump(comparisonData.historical.configuration || {}, yamlDumpOptions)}
                    newValue={yaml.dump(comparisonData.active.configuration || {}, yamlDumpOptions)}
                    splitView={true}
                    styles={diffViewerStyles}
                    leftTitle={`Version from ${new Date(comparisonData.historical.created_at).toLocaleString()}`}
                    rightTitle={"Active Version"}
                  />
                </Box>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onCompareClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ConfigAdmin;